/**
 * F38. Panel lateral de un KPI: se abre al pulsar una tarjeta.
 *
 * Antes solo listaba las cuentas. La fórmula y los umbrales estaban en un `title` del icono ⓘ,
 * como un texto suelto («Verde: 1,5–2,2. Amarillo: 1,0–1,5 o > 2,2…»), y no se veía por qué un
 * valor está en el color en que está. Ahora, como en Ledgeria, el panel cuenta el KPI entero:
 *
 *   1. qué mide,
 *   2. qué está pasando AHORA (el mismo mensaje que la tarjeta),
 *   3. la fórmula,
 *   4. los umbrales de CADA color con su significado, marcando en cuál está el valor actual,
 *   5. las cuentas contables que lo componen, agrupadas por variable con su subtotal.
 *
 * ☠️ Las bandas del punto 4 salen de `semaforoBands` (@jr2os/shared), generadas de la misma tabla
 * con la que el backend decide el color: la lista que se enseña y el color que se pinta no pueden
 * contradecirse (`smoke-semaforo-bandas` lo comprueba).
 */

import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  bandContains,
  semaforoBands,
  type AccountRow,
  type BandRange,
  type Ratio,
  type Semaforo,
  type SemaforoBandKey,
} from '@jr2os/shared'
import { currentLocaleTag } from '../../lib/formatting'
import { formatCents } from '../../lib/money'

export const STATUS_COLOR: Record<Semaforo, string> = {
  green: '#16a34a',
  yellow: '#d97706',
  red: '#dc2626',
  na: '#64748b',
}

export type KpiFormat = 'ratio' | 'pct' | 'cents'

export interface KpiDetail {
  ratio: Ratio
  ratioKey: SemaforoBandKey
  format: KpiFormat
  /** El número tal como lo enseña la tarjeta. */
  valueText: string
  /** El mensaje de la tarjeta para el valor actual (ya traducido; puede venir vacío). */
  why: string
}

/** Las cuentas de UNA variable del ratio (AC, PC…). */
export interface DrilldownGroup {
  key: string
  accounts: AccountRow[]
}

interface KpiDrilldownDrawerProps {
  open: boolean
  onClose: () => void
  title: string
  detail: KpiDetail | null
  groups: DrilldownGroup[]
}

function formatNumber(n: number, format: KpiFormat, locale: string): string {
  if (format === 'pct') {
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(n)} %`
  }
  return new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 2 }).format(n)
}

export default function KpiDrilldownDrawer({
  open,
  onClose,
  title,
  detail,
  groups,
}: KpiDrilldownDrawerProps) {
  const { t } = useTranslation('accounting')
  const locale = currentLocaleTag()
  const accountCount = groups.reduce((n, g) => n + g.accounts.length, 0)

  const ratio = detail?.ratio ?? null
  const color = ratio ? STATUS_COLOR[ratio.status] : STATUS_COLOR.na
  const bands = detail ? semaforoBands(detail.ratioKey) : []
  // La PRIMERA banda que contiene el valor (la mejor si cae en un borde), como `computeStatus`.
  // Por posición: `semaforoBands` crea objetos nuevos en cada llamada.
  const activeIndex =
    ratio && ratio.value !== null
      ? bands.findIndex((b) => bandContains(b.range, ratio.value as number))
      : -1
  // Los dos crecimientos (mes/mes y año/año) comparten textos.
  const bandGroup =
    detail && (detail.ratioKey === 'salesGrowthMoM' || detail.ratioKey === 'salesGrowthYoY')
      ? 'salesGrowth'
      : (detail?.ratioKey ?? '')

  const rangeText = (range: BandRange): string => {
    const f = (n: number) => formatNumber(n, detail?.format ?? 'ratio', locale)
    switch (range.kind) {
      case 'between':
        return t('kpiDetail.range.between', { from: f(range.from), to: f(range.to) })
      case 'above':
        return t('kpiDetail.range.above', { value: f(range.value) })
      case 'atLeast':
        return t('kpiDetail.range.atLeast', { value: f(range.value) })
      case 'below':
        return t('kpiDetail.range.below', { value: f(range.value) })
      case 'atMost':
        return t('kpiDetail.range.atMost', { value: f(range.value) })
      case 'positive':
        return t('kpiDetail.range.positive')
      case 'nonPositive':
        return t('kpiDetail.range.nonPositive')
    }
  }

  return (
    <>
      {/* Overlay */}
      <button
        type="button"
        aria-label={t('kpiDrilldown.close')}
        onClick={onClose}
        className={[
          'fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-200',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        ].join(' ')}
      />

      {/* Panel */}
      <aside
        className={[
          'fixed top-0 right-0 z-50 h-screen w-full max-w-lg flex flex-col',
          'border-l border-[var(--border)] bg-[var(--bg-panel)] shadow-2xl',
          'transition-transform duration-300',
          open ? 'translate-x-0' : 'translate-x-full',
        ].join(' ')}
        style={{ background: 'var(--bg-panel, var(--bg-input-soft))' }}
        aria-hidden={!open}
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[var(--border)]">
          <div className="min-w-0">
            <h2 className="font-semibold t-primary flex items-center gap-2">
              {detail && (
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: color }}
                  aria-hidden
                />
              )}
              <span className="truncate">{detail ? detail.ratio.label : title}</span>
            </h2>
            {detail && (
              <div className="text-3xl font-semibold font-mono mt-1" style={{ color }}>
                {detail.valueText}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('kpiDrilldown.close')}
            className="p-1.5 rounded-md hover:bg-[var(--bg-hover)] t-secondary"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {detail && ratio && (
            <div className="px-5 py-4 space-y-5 border-b border-[var(--border)]">
              <Section title={t('kpiDetail.what')}>
                <p className="text-sm t-secondary leading-snug">{ratio.tooltip}</p>
              </Section>

              {(ratio.status === 'na' || detail.why) && (
                <div
                  className="rounded-lg border-l-4 bg-[var(--bg-input-soft)] px-3 py-2.5"
                  style={{ borderLeftColor: color }}
                >
                  <div
                    className="text-[11px] font-semibold uppercase tracking-wider"
                    style={{ color }}
                  >
                    {t(`insight.status.${ratio.status}`)}
                  </div>
                  <p className="text-sm t-secondary leading-snug mt-0.5">
                    {detail.why || t('tooltip.divByZero')}
                  </p>
                </div>
              )}

              <Section title={t('kpiDetail.formula')}>
                <code className="block rounded-md bg-[var(--bg-input-soft)] px-3 py-2 text-sm font-mono t-primary">
                  {ratio.formula}
                </code>
              </Section>

              <Section title={t('kpiDetail.thresholds')}>
                <ul className="space-y-1.5">
                  {bands.map((b, i) => {
                    const isActive = i === activeIndex
                    return (
                      <li
                        key={i}
                        className={[
                          'flex items-start gap-2 text-sm rounded-md px-2 py-1 -mx-2',
                          isActive ? 'bg-[var(--bg-hover)]' : '',
                        ].join(' ')}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0 mt-1.5"
                          style={{ background: STATUS_COLOR[b.color] }}
                          aria-hidden
                        />
                        <span className="t-secondary leading-snug">
                          <span className={isActive ? 'font-semibold t-primary' : 't-primary'}>
                            {rangeText(b.range)}
                          </span>
                          {' → '}
                          {t(`kpiDetail.band.${bandGroup}.${b.meaning}`)}
                          {isActive && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider t-muted">
                              {t('kpiDetail.youAreHere')}
                            </span>
                          )}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </Section>
            </div>
          )}

          <div className="px-5 pt-4">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider t-muted">
              {t('kpiDetail.accounts')}
            </h3>
            <p className="text-xs t-faint mt-0.5">
              {accountCount}{' '}
              {accountCount === 1
                ? t('kpiDrilldown.accountSingular')
                : t('kpiDrilldown.accountPlural')}
            </p>
          </div>

          {accountCount === 0 ? (
            <p className="px-5 py-8 text-sm t-muted text-center">{t('kpiDrilldown.emptyState')}</p>
          ) : (
            <div className="pb-6">
              {groups
                .filter((g) => g.accounts.length > 0)
                .map((g) => {
                  const subtotal = g.accounts.reduce((acc, a) => acc + a.amount, 0)
                  return (
                    <div key={g.key} className="mt-3">
                      <div className="flex items-center justify-between gap-3 px-5 py-1.5 bg-[var(--bg-hover)]">
                        <span className="text-xs font-semibold t-primary">
                          {t(`kpiDetail.vars.${g.key}`, { defaultValue: g.key })}{' '}
                          <span className="font-mono t-muted">({g.key})</span>
                        </span>
                        <span className="font-mono text-xs font-semibold t-primary whitespace-nowrap">
                          {formatCents(subtotal)}
                        </span>
                      </div>
                      <table className="w-full text-sm">
                        <tbody>
                          {g.accounts.map((a) => (
                            <tr key={a.code} className="border-t border-[var(--border)]/60">
                              <td className="pl-5 pr-2 py-2 font-mono text-xs t-muted whitespace-nowrap align-top">
                                {a.code}
                              </td>
                              <td className="px-2 py-2 t-secondary">{a.name}</td>
                              <td className="pl-2 pr-5 py-2 text-right font-mono t-primary whitespace-nowrap align-top">
                                {formatCents(a.amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                })}
            </div>
          )}
        </div>
      </aside>
    </>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[11px] font-semibold uppercase tracking-wider t-muted mb-1.5">{title}</h3>
      {children}
    </section>
  )
}
